import { useContext, useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ScenarioStatesProviderContext } from '../providers/StatesProvider'
import useNavigation from './useNavigation'
import SimulateService from '../../../../service/SimulateService'
import { getApiFailureMessage, navigateToWithState, showToastMessage } from '../../../../utils/commonFunctions'
import useBaseApi from './useBaseApi'
import useStepper from './useStepper'
import { getInitialFormData } from '../utils/constants';
import {
    ExecutionLoaderType,
    FaultReportType,
    ScenarioFormDataType,
    ScheduleTaskType,
    SelectedTaskplansType
} from '../utils/scenario-types'
import {
    formatOrbitalInsightsData,
    formatPresimData,
    getFormattedFaultsPlanData,
    getFormattedScheduleData,
    getViewerPacketDetails,
    resetValidatePresimData,
} from '../utils/helperFunction'
import MissionService from '../../../../service/MissionService'
import { SCENARIO_STEPS, SCENARIO_DEPLOYMENT_TYPES, SCENARIO_OPERATION_TYPES, SCENARIO_STATUS } from '../../constants'
import { ScenarioResponseType } from '../../types'
import { createGroundPacket, createSatellitePacket } from '../utils/viewer-packets'
import { getOrbitalInsights } from '../../../CommonComponents/orbital-insights/api'
import { OrbitalInsightSatelliteType } from '../../../CommonComponents/orbital-insights/orbital-insights-type'
import { removeDataSourceByName } from '../../../../utils/cesiumUtil'
import useFaultApis from './useFaultApis'
import useReportWindow from './useReportWindow'
import useStaticData from './useStaticData'
import useTimelineFilter from './useTimelineFilter'
import {
    getScenarioSetupMessage,
    getScenarioSetupStatus,
    SCENARIO_SETUP_STATUS
} from '../utils/satellite-groups-add'
import useSatelliteGroupsAdd from './useSatelliteGroupsAdd'

const useStepApi = () => {
    const params = useParams()
    const navigate = useNavigate()
    const { mission_name, mission_id } = params as { mission_name: string, mission_id: string }
    const { missionId, scenarioId, closeScenario, getQueryParam, resetViewerMode } = useNavigation()
    const context = useContext(ScenarioStatesProviderContext)
    if (context === undefined) {
        throw new Error('useStepApi must be used within a Scenario States Provider')
    }
    const { getScenarioDetails } = useBaseApi()
    const { updateScenarioStep } = useStepper()
    const { getallFaultReportData } = useFaultApis()
    const { resetTimelineFilter } = useTimelineFilter()
    const { closeAllReportModals, closeReportModalWindows } = useReportWindow()
    const { allStations, allFaults, allTaskPlans, allGeoFeatures } = useStaticData()
    const { viewer, formData, scenarioResponse, setFormData, setLoading, setScenarioResponse, setScenarioBaseData, setSelectedGroupSatellite, selectedGroupSatellite, simulatorList, selectedSimulator, scenarioJson } = context
    const { completeSatelliteGroupsAdd } = useSatelliteGroupsAdd()
    const scheduleGenerationRequestInProgressRef = useRef(false)

    const startLoading = (state: ExecutionLoaderType) => {
        setLoading((prev) => ({
            ...prev,
            execution: state,
            executionMessage: state && state === prev.execution ? prev.executionMessage : false
        }))
    }

    const createScenario = async () => {
        if (!missionId) return
        const scenarioOperation = formData.details.scenarioOperation

        const isManual = scenarioOperation === SCENARIO_OPERATION_TYPES.MANUAL

        if (isManual) {
            startLoading('Deploying')
            if (!scenarioJson) {
                showToastMessage('error', 'Please upload a scenario config JSON before creating')
                startLoading(false)
                return
            }
            try {
                const firstSelectedSimName = Object.values(selectedSimulator)[0] || ''
                const simulatorId = simulatorList.find((s) => s.simulatorName === firstSelectedSimName)?.simulatorId || simulatorList[0]?.simulatorId
                const manualPayload = new FormData()
                manualPayload.append('scenarioName', formData.details.scenarioName)
                manualPayload.append('simulatorId', String(simulatorId))
                manualPayload.append('operationType', scenarioOperation)
                manualPayload.append('simulationConfig', new Blob([JSON.stringify(scenarioJson)], { type: 'application/json' }), 'scenario.json')
                const response = await SimulateService.createManualScenario(missionId, manualPayload)
                const deploymentResponse = await SimulateService.deployManualScenario(missionId, response?.data?.data?.scenarioId)
                const scenario: ScenarioResponseType = response?.data?.data
                if (scenario) {
                    setScenarioResponse(scenario)
                    updateScenarioStep(SCENARIO_STEPS.SATELLITES, scenario?.scenarioId)
                    // Show toast on destination page after loading
                    const successMessage = deploymentResponse?.data?.message
                    resetViewerMode()
                    navigateToWithState(
                        `${mission_name}/${mission_id}/simulate`,
                        navigate,
                        { deploySuccessMessage: successMessage }
                    )
                }
            } catch (error) {
                const message = getApiFailureMessage(error, 'Create');
                showToastMessage('error', message);
            } finally {
                startLoading(false)
            }
            return
        }

        startLoading('Creating')

        const body = {
            scenarioName: formData.details.scenarioName,
            sampleRate: formData.details.sampleRate,
            scenarioOperation: formData.details.scenarioOperation,
            scenarioDeploymentType: formData.details.scenarioDeploymentType,
            scenarioStartTime: formData.details.scenarioStartTime
        }
        try {
            const response = await SimulateService.createScenario(missionId, body)
            const scenario: ScenarioResponseType = response.data.data?.[0]
            setScenarioResponse(scenario)
            setFormData((prev) => {
                const data = {
                    ...prev,
                    segments: {
                        ...prev.segments,
                        stations: scenario?.scenarioOperation === SCENARIO_OPERATION_TYPES.LEOP ? [] : prev.segments.stations
                    },
                    schedule: {
                        ...prev.schedule,
                        simulationStartTime: scenario?.simulationStartTime,
                    },
                }
                if (scenario?.scenarioOperation === SCENARIO_OPERATION_TYPES.LEOP) {
                    removeDataSourceByName(viewer, "GS_OBJECT");
                    removeDataSourceByName(viewer, "MULTI_GROUND_OBJECT");
                }
                return data
            })
            updateScenarioStep(SCENARIO_STEPS.SATELLITES, scenario?.scenarioId)
        } catch (error) {
            const message = getApiFailureMessage(error, 'Create');
            showToastMessage('error', message);
        } finally {
            startLoading(false)
        }
    }

    const updateScenario = async () => {
        if (!scenarioId) return
        startLoading('Updating')
        const body = {
            scenarioName: formData.details.scenarioName,
            sampleRate: formData.details.sampleRate,
            scenarioOperation: formData.details.scenarioOperation,
            scenarioDeploymentType: formData.details.scenarioDeploymentType,
            scenarioStartTime: formData.details.scenarioStartTime
        }
        try {
            await SimulateService.patchScenarioName(missionId, scenarioId, body)
            let scenario = await getScenarioDetails(missionId, scenarioId)
            const sameScenarioOptionType = scenario?.scenarioOperation === scenarioResponse?.scenarioOperation
            const sameScenarioDeploymentType = scenario?.scenarioDeploymentType === scenarioResponse?.scenarioDeploymentType
            const sameScenarioStartTime = scenario?.scenarioStartTime === scenarioResponse?.scenarioStartTime
            const resetForm = !sameScenarioOptionType || !sameScenarioDeploymentType || !sameScenarioStartTime
            const INITIAL_FORM_DATA = getInitialFormData()

            if (resetForm) {
                setFormData((prev) => {
                    const data: ScenarioFormDataType = {
                        ...prev,
                        satellites: INITIAL_FORM_DATA.satellites,
                        segments: {
                            ...prev.segments,
                            stations: scenario?.scenarioOperation === SCENARIO_OPERATION_TYPES.LEOP ? [] : prev.segments.stations
                        },
                        hwil: sameScenarioDeploymentType ? prev.hwil : INITIAL_FORM_DATA.hwil,
                        schedule: {
                            ...prev.schedule,
                            taskplans: INITIAL_FORM_DATA.schedule.taskplans,
                            simulationStartTime: scenario?.simulationStartTime,
                            simulationDuration: scenario?.simulationDuration
                        },
                        faults: INITIAL_FORM_DATA.faults,
                        assets: {
                            ...prev?.assets,
                            schedule: INITIAL_FORM_DATA.assets.schedule,
                            faultPlans: INITIAL_FORM_DATA.assets.faultPlans,
                            presim: INITIAL_FORM_DATA.assets.presim,
                            hwilAssets: INITIAL_FORM_DATA.assets.hwilAssets,
                            orbitalInsights: INITIAL_FORM_DATA.assets.orbitalInsights
                        },
                    }
                    const { allGroundStations, selectedStations, selectedTaskplans, selectedFaultPlans } = getViewerPacketDetails(data, allTaskPlans, allGeoFeatures, allFaults, allStations)
                    createGroundPacket(viewer, allGroundStations, selectedStations, selectedTaskplans, selectedFaultPlans);
                    removeDataSourceByName(viewer, "ORBIT_OBJECT");
                    return data
                })
                scenario = {
                    ...scenario,
                    satelliteGroups: [],
                    groundStations: [],
                }
                setSelectedGroupSatellite(null)
                closeAllReportModals()
            }
            setScenarioResponse(scenario)
            updateScenarioStep(SCENARIO_STEPS.SATELLITES)
        } catch (error: any) {
            const message = getApiFailureMessage(error, 'Update');
            showToastMessage('error', message)
        } finally {
            startLoading(false)
        }
    }

    const patchSatellite = async () => {
        startLoading('Plotting')
        const body = {
            "satelliteGroups": (formData.satellites).map((sat) => ({
                "groupId": sat?.groupId as string,
                'satelliteId': sat?.satelliteId,
                "referenceOrbit": sat?.referenceOrbit,
                "color": sat?.color,
                "phasingAngle": sat?.phasingAngle,
                "phasingType": sat?.phasingType
            }))
        };
        try {
            const response = await SimulateService.updateScenarioWithSatelliteGroups(missionId, scenarioId, body)
            const message = getScenarioSetupMessage(response)
            const setupStatus = getScenarioSetupStatus(response)
            if (setupStatus === SCENARIO_SETUP_STATUS.SATELLITE_GROUPS_ADD_FAILED) {
                throw new Error(message)
            }
            if (setupStatus) {
                setScenarioResponse((prev) => prev ? {
                    ...prev,
                    setupStatus,
                    setupStatusMessage: message
                } : prev)
            }
            setLoading((prev) => ({
                ...prev,
                execution: 'Plotting',
                executionMessage: message || false
            }))
            removeDataSourceByName(viewer, "ORBIT_OBJECT");
            closeAllReportModals()
            if (setupStatus === SCENARIO_SETUP_STATUS.SATELLITE_GROUPS_ADD_COMPLETED) {
                await completeSatelliteGroupsAdd()
            }
        } catch (error) {
            const message = (error as any)?.response ? getApiFailureMessage(error, 'Update') : (error as Error)?.message || getApiFailureMessage(error, 'Update');
            showToastMessage('error', message)
            startLoading(false)
        }
    }

    const updateHwilConfiguration = async () => {
        startLoading('Updating')
        const appData = formData.hwil.remoteAppConfiguration.remoteApp.AppData.map(payload => {
            const updated_Payload = { ...payload }
            delete updated_Payload.interfaces
            return updated_Payload
        })
        const body = {
            "hwInfo": formData.hwil.hwInfo,
            "remoteAppConfiguration": {
                ...formData.hwil.remoteAppConfiguration,
                "remoteApp": {
                    ...formData.hwil.remoteAppConfiguration.remoteApp,
                    "AppData": appData
                }
            }
        }
        try {
            await SimulateService.updateHWILConfiguration(missionId, scenarioId, body)
            const scenario = await getScenarioDetails(missionId, scenarioId as string)
            const INITIAL_FORM_DATA = getInitialFormData()
            setScenarioResponse(scenario)
            setFormData((prev) => ({
                ...prev,
                hwil: {
                    ...prev?.hwil,
                    hwInfo: scenario?.hwInfo || [],
                },
                assets: {
                    ...prev?.assets,
                    hwilAssets: INITIAL_FORM_DATA.assets.hwilAssets,
                }
            }))
            updateScenarioStep(SCENARIO_STEPS.SEGMENTS)
        } catch (error) {
            const message = getApiFailureMessage(error, 'Update');
            showToastMessage('error', message)
        } finally {
            startLoading(false)
        }
    }

    const updateAttitudeCondition = async () => {
        startLoading('Updating')
        try {
            const response = await SimulateService.addInitialAttitudeCondition(missionId, scenarioId, formData?.initial_altitude)
            const scenario = response?.data
            const INITIAL_FORM_DATA = getInitialFormData()
            setScenarioResponse(scenario)
            setFormData((prev) => ({
                ...prev,
                assets: {
                    ...prev?.assets,
                    schedule: INITIAL_FORM_DATA.assets.schedule,
                    presim: {
                        ...prev?.assets?.presim,
                        data: formatPresimData(prev?.assets?.presim?.data, scenario?.satelliteGroups)
                    }
                }
            }))
            closeAllReportModals()
            updateScenarioStep(SCENARIO_STEPS.SCHEDULE)
        } catch (error) {
            const message = getApiFailureMessage(error, 'Update');
            showToastMessage('error', message)
        } finally {
            startLoading(false)
        }
    }

    const patchGroundStations = async () => {
        startLoading('Updating')
        try {
            const stations = formData.segments.stations || []
            const response = await SimulateService.updateScenarioWithGroundStations(missionId, scenarioId, stations)
            const scenario = response.data
            const isTrueTwinCommandCenter = scenario?.scenarioDeploymentType === SCENARIO_DEPLOYMENT_TYPES.TRUETWIN_COMMAND_CENTER;
            const INITIAL_FORM_DATA = getInitialFormData()

            // Skip orbital insights for TRUETWIN_COMMAND_CENTER as it doesn't use SCHEDULE step
            let orbitalInsights: Record<string, OrbitalInsightSatelliteType> | null = null;
            if (!isTrueTwinCommandCenter) {
                const orbitalInsightsResponse = await getOrbitalInsights(missionId, scenarioId!);
                orbitalInsights = formatOrbitalInsightsData(orbitalInsightsResponse);
            }

            setScenarioResponse(scenario)
            setFormData((prev) => ({
                ...prev,
                assets: {
                    ...prev?.assets,
                    schedule: INITIAL_FORM_DATA.assets.schedule,
                    orbitalInsights: isTrueTwinCommandCenter ? prev.assets.orbitalInsights : {
                        status: 'completed',
                        data: orbitalInsights
                    }
                }
            }))
            closeAllReportModals()
            const scenarioDeploymentType = scenario?.scenarioDeploymentType
            const segmentNextStep = scenarioDeploymentType === SCENARIO_DEPLOYMENT_TYPES.TRUETWIN_COMMAND_CENTER ? SCENARIO_STEPS.DEPLOY : SCENARIO_STEPS.SCHEDULE
            updateScenarioStep(segmentNextStep)
        } catch (error) {
            const message = getApiFailureMessage(error, 'Update');
            showToastMessage('error', message)
        } finally {
            startLoading(false)
        }
    }

    const getGeneratedSchedule = async (groups: string[], taskplans: SelectedTaskplansType[], simulationstartTime: number, duration: number, scenarioOption: string) => {
        if (scheduleGenerationRequestInProgressRef.current) return
        scheduleGenerationRequestInProgressRef.current = true
        let isAsyncFlow = false
        try {
            let ScheduleResponseData: any = null
            let schedules: Record<string, ScheduleTaskType[]> = {}
            const updatedTaskPlans: Record<string, string> = {}
            taskplans.map(tp => {
                updatedTaskPlans[tp.satelliteGroupId] = tp.taskPlanId
            })
            const path = `startEpoch=${simulationstartTime}&duration=${duration}`
            const body = groups.map(gp => ({
                "satelliteGroupId": gp,
                "taskPlanId": updatedTaskPlans[gp] || ''
            }))
            if (scenarioOption === SCENARIO_OPERATION_TYPES.LEOP) {
                startLoading('Analyzing')
                await SimulateService.generateLeOpsSchedule(missionId, scenarioId, path, body)
            } else {
                // CONOPS: fire-and-forget — WebSocket SCENARIO_SCHEDULE_GENERATION handles completion
                startLoading('Generating')
                const response = await SimulateService.generateSchedule(missionId, scenarioId, path, body)
                const message = getScenarioSetupMessage(response)
                const setupStatus = getScenarioSetupStatus(response)
                if (setupStatus === SCENARIO_SETUP_STATUS.ONBOARD_SCHEDULE_GENERATE_FAILED) {
                    throw new Error(message)
                }
                if (setupStatus) {
                    setScenarioResponse((prev) => prev ? {
                        ...prev,
                        setupStatus,
                        setupStatusMessage: message
                    } : prev)
                }
                setFormData((prev) => ({
                    ...prev,
                    assets: {
                        ...prev.assets,
                        schedule: {
                            ...prev.assets.schedule,
                            status: 'running'
                        }
                    }
                }))
                setLoading((prev) => ({
                    ...prev,
                    execution: 'Generating',
                    executionMessage: message || false
                }))
                isAsyncFlow = true
                return
            }
            // Below runs only for LEOP (synchronous path)
            const notSameTimeDuration = scenarioResponse?.simulationStartTime !== simulationstartTime || scenarioResponse?.simulationDuration !== duration
            let updatedFaultPlans: FaultReportType = null
            if (notSameTimeDuration) {
                const body = formData?.satellites.map(satellite => ({
                    satelliteGroupId: satellite?.groupId,
                    faultPlanId: ''
                }))
                const response = await MissionService.postFaultScheduleData(missionId, scenarioId, body)
                updatedFaultPlans = getFormattedFaultsPlanData(response?.data?.data)
            }
            const scenario = await getScenarioDetails(missionId, scenarioId as string)
            const selectedGroup = scenario?.satelliteGroups[0]
            const selectedSatellite = selectedGroupSatellite?.satelliteId || selectedGroup?.satellites[0].satelliteId
            resetTimelineFilter(schedules, selectedSatellite)
            setScenarioResponse(scenario)
            setFormData((prev) => {
                const data = {
                    ...prev,
                    faults: notSameTimeDuration ? [] : prev?.faults,
                    assets: {
                        ...prev?.assets,
                        presim: {
                            ...prev?.assets?.presim,
                            data: formatPresimData(prev?.assets?.presim?.data, scenario?.satelliteGroups)
                        },
                        schedule: {
                            status: 'completed',
                            data: schedules,
                            allSchedules: ScheduleResponseData
                        },
                        faultPlans: notSameTimeDuration ? {
                            status: 'invalidated',
                            data: updatedFaultPlans
                        } : prev?.assets?.faultPlans,
                    }
                }
                return data as ScenarioFormDataType
            })
            const sameSimulationTime = scenario?.simulationStartTime === simulationstartTime
            const sameDuration = scenario?.simulationDuration === duration
            if (!sameSimulationTime || !sameDuration) {
                await createSatellitePacket(viewer, scenarioId, missionId, selectedSatellite)
            }
            updateScenarioStep(SCENARIO_STEPS.FAULTS)
        } catch (error) {
            if (scenarioOption === SCENARIO_OPERATION_TYPES.CONOPS) {
                setFormData((prev) => ({
                    ...prev,
                    assets: {
                        ...prev.assets,
                        schedule: {
                            ...prev.assets.schedule,
                            status: 'invalidated'
                        }
                    }
                }))
            }
            const message = (error as any)?.response
                ? getApiFailureMessage(error, 'Update')
                : (error as Error)?.message || getApiFailureMessage(error, 'Update');
            showToastMessage('error', message)
        } finally {
            scheduleGenerationRequestInProgressRef.current = false
            if (!isAsyncFlow) startLoading(false)
        }
    }
    const getImportedSchedule = async (data: any, simulationstartTime: number, duration: number) => {
        startLoading('Generating')
        try {
            await SimulateService.importSchedule(missionId, scenarioId, data, simulationstartTime, duration)
            const response = await SimulateService.generateImportedSchedule(missionId, scenarioId)
            const scenario = await getScenarioDetails(missionId, scenarioId as string)
            const schedules: Record<string, ScheduleTaskType[]> = getFormattedScheduleData(response.data)
            const orbitalInsightsResponse = await getOrbitalInsights(missionId, scenarioId!);
            const orbitalInsights = formatOrbitalInsightsData(orbitalInsightsResponse);
            const selectedGroup = scenario?.satelliteGroups[0]
            const selectedSatellite = selectedGroupSatellite?.satelliteId || selectedGroup?.satellites[0].satelliteId
            setScenarioResponse(scenario)
            resetTimelineFilter(schedules, selectedSatellite)
            setFormData((prev) => ({
                ...prev,
                assets: {
                    ...prev?.assets,
                    presim: {
                        ...prev?.assets?.presim,
                        data: resetValidatePresimData(prev?.assets?.presim?.data)
                    },
                    schedule: {
                        status: 'completed',
                        data: schedules,
                        allSchedules: response.data
                    },
                    orbitalInsights: {
                        status: orbitalInsights ? 'completed' : 'idle',
                        data: orbitalInsights
                    }
                }
            }))
            const sameSimulationTime = scenario?.simulationStartTime === simulationstartTime
            const sameDuration = scenario?.simulationDuration === duration
            if (!sameSimulationTime || !sameDuration) {
                const selectedGroup = scenario?.satelliteGroups[0]
                const selectedSatellite = selectedGroup?.satellites[0].satelliteId
                await createSatellitePacket(viewer, scenarioId, missionId, selectedSatellite)
            }
            updateScenarioStep(SCENARIO_STEPS.FAULTS)
        } catch (error) {
            const message = getApiFailureMessage(error, 'Update');
            showToastMessage('error', message)
        } finally {
            startLoading(false)
        }
    }


    const postFaultInjection = async () => {

        const updatedFaults: Record<string, string> = {}
        const selectedFaults = formData?.faults || []
        const selectedGroups = (formData?.satellites || []).map(sat => sat.groupId)

        selectedFaults?.map(fault => {
            updatedFaults[fault?.satelliteGroupId] = fault?.faultPlanId
        })

        const body = selectedGroups.map(gp => ({
            satelliteGroupId: gp,
            faultPlanId: updatedFaults[gp] || ''
        }))

        startLoading('Injecting')
        try {
            const response = await MissionService.postFaultScheduleData(missionId, scenarioId, body)
            const scenario = await getScenarioDetails(missionId, scenarioId as string)
            const orbitalInsightsResponse = await getOrbitalInsights(missionId, scenarioId!);
            const faults: FaultReportType = getFormattedFaultsPlanData(response?.data?.data)
            const orbitalInsights = formatOrbitalInsightsData(orbitalInsightsResponse);
            const deploymentType = scenario?.scenarioDeploymentType
            setScenarioResponse(scenario)
            setFormData((prev) => ({
                ...prev,
                assets: {
                    ...prev?.assets,
                    faultPlans: {
                        status: 'completed',
                        data: faults
                    },
                    orbitalInsights: {
                        status: orbitalInsights ? 'completed' : 'idle',
                        data: orbitalInsights
                    }
                }
            }))
            const assetNextStep = deploymentType === SCENARIO_DEPLOYMENT_TYPES.HARDWARE_IN_LOOP ? scenario?.status === SCENARIO_STATUS.HWIL_CONFIG_GENERATED ? SCENARIO_STEPS.CONNECT : SCENARIO_STEPS.ASSETS : SCENARIO_STEPS.DEPLOY
            updateScenarioStep(assetNextStep)
        } catch (error) {
            const message = getApiFailureMessage(error, 'Update');
            showToastMessage('error', message)
        } finally {
            startLoading(false)
        }
    }

    const updateScheduleDetails = async () => {
        closeReportModalWindows('presimReport')
        const scheduleType = formData.schedule.scheduleType
        const importedFile = formData.schedule.importedSchedule?.data
        const groups = formData.satellites.map(sat => sat.groupId)
        const taskplans = formData.schedule.taskplans
        const simulationStartTime = formData.schedule.simulationStartTime
        const simulationDuration = formData.schedule.simulationDuration
        const scenarioOption = formData?.details?.scenarioOperation
        if (scheduleType === 'import') {
            await getImportedSchedule(importedFile, simulationStartTime, simulationDuration)
        } else {
            await getGeneratedSchedule(groups, taskplans, simulationStartTime, simulationDuration, scenarioOption)
        }
    }


    const deleteScenario = async () => {
        try {
            startLoading('Deleting')
            await SimulateService.deleteScenario(missionId, scenarioId)
            closeScenario()
        } catch (error) {
            const message = getApiFailureMessage(error, 'Update');
            showToastMessage('error', message)
        } finally {
            startLoading(false)
        }
    }


    const deployScenario = async () => {
        startLoading("Deploying")
        try {
            const deployScenarioResponse = await SimulateService.deployScenario(missionId, scenarioId)
            const scenarioStep = getQueryParam('step')
            if (scenarioResponse?.scenarioDeploymentType !== SCENARIO_DEPLOYMENT_TYPES.HARDWARE_IN_LOOP && scenarioStep === SCENARIO_STEPS.DEPLOY) {
                // Show Toast on destination page so user sees it after simulate page loads
                const successMessage = deployScenarioResponse?.data?.message;
                resetViewerMode() // Same as closeScenario does
                navigateToWithState(
                    `${mission_name}/${mission_id}/simulate`,
                    navigate,
                    { deploySuccessMessage: successMessage }
                )
            }
        } catch (error: any) {
            startLoading(false)
            const message = scenarioResponse?.scenarioDeploymentType === SCENARIO_DEPLOYMENT_TYPES.HARDWARE_IN_LOOP ? 'Failed to Connect to device' : error?.response?.data?.message || "Failed to Deploy Scenario"
            const scenarioStep = getQueryParam('step')
            if (scenarioStep === SCENARIO_STEPS.DEPLOY || scenarioStep === SCENARIO_STEPS.CONNECT) {
                showToastMessage('error', message);
            }
        }
    };

    const undeployScenario = () => {
        setLoading((prev) => ({
            ...prev,
            undeploy: 'Undeploying'
        }))
        SimulateService.unDeployScenario(missionId, scenarioId)?.then((res) => {
            if (res?.data) {
                closeScenario()
            }
        }).catch((err) => {
            showToastMessage('error', "Something went wrong During Undeploy")
        }).finally(() => {
            setLoading((prev) => ({
                ...prev,
                undeploy: false
            }))
        })
    }

    return {
        createScenario,
        updateScenario,
        patchSatellite,
        updateHwilConfiguration,
        patchGroundStations,
        updateAttitudeCondition,
        updateScheduleDetails,
        postFaultInjection,
        deleteScenario,
        deployScenario,
        undeployScenario
    }
}

export default useStepApi
